import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  LoadingSkeleton,
  ProgressBar,
} from '@nexora/design-system';
import { useAccrualDetail } from './useAccrualDetail';
import { formatCurrency, formatDate, formatCount, EMPTY } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_motor_de_c_lculo_e_apura_o_detalhada`
   → Motor de Cálculo e Apuração Detalhada — GET /fiscal/accrual.

   Da referência vieram: os resumos de receita/regime ("Revenue - Regime
   Summary"), o período de apuração ("Q3 2024 Accrual Period"), a tabela de
   obrigações calculadas (Base Value, Rate %, Gross Tax) e o destaque
   "From N valid NF-e inputs" (que aqui vira o engineAudit).

   Não vieram (não existem no contrato): créditos de entrada
   ("Input ICMS Credits", "PIS/COFINS Credits"), "Net Tax Liability" como
   linha própria (o contrato traz `totals.totalTaxes`), nem débitos por
   documento individual.

   O motor roda de verdade aqui: `engineUsed` indica se os itens foram
   recalculados; o fallback usa os totais dos documentos autorizados.
   ─────────────────────────────────────────────────────────────────────────── */

const REGIME_OPTIONS = [
  { value: '', label: 'Padrão (Lucro Presumido)' },
  { value: 'SIMPLES_NACIONAL', label: 'Simples Nacional' },
  { value: 'LUCRO_PRESUMIDO', label: 'Lucro Presumido' },
  { value: 'LUCRO_REAL', label: 'Lucro Real' },
];

const OPERATION_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'VENDA', label: 'Venda' },
  { value: 'COMPRA', label: 'Compra' },
  { value: 'DEVOLUCAO_VENDA', label: 'Devolução de venda' },
  { value: 'DEVOLUCAO_COMPRA', label: 'Devolução de compra' },
  { value: 'TRANSFERENCIA', label: 'Transferência' },
  { value: 'SERVICO', label: 'Serviço' },
  { value: 'OUTROS', label: 'Outros' },
];

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO',
];

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return EMPTY;
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

export function AccrualDetailPage(): JSX.Element {
  const hoje = new Date();
  const competenciaAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

  const [competencia, setCompetencia] = useState(competenciaAtual);
  const [regime, setRegime] = useState('');
  const [operationType, setOperationType] = useState('');
  const [originUf, setOriginUf] = useState('');
  const [destinationUf, setDestinationUf] = useState('');

  const { data, isLoading, error, refetch } = useAccrualDetail({
    competencia,
    regime: regime || undefined,
    operationType: operationType || undefined,
    originUf: originUf || undefined,
    destinationUf: destinationUf || undefined,
  });

  const obligations = data?.calculatedObligations ?? [];
  const byCfop = data?.revenue.byCfop ?? [];
  const enginesErrors = data?.regimeSummary.engineErrors ?? [];

  const maxObligation = obligations.reduce((max, o) => Math.max(max, o.grossTax), 0);

  return (
    <div className="accrual-detail-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Motor de Cálculo e Apuração Detalhada
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Apuração calculada pelo motor tributário real sobre os documentos autorizados do período.
          </p>
        </div>
        <div className="accrual-detail__period">
          <span className="font-label-md text-label-md text-on-surface-variant">
            Período: {formatDate(data?.period.startDate)} — {formatDate(data?.period.endDate)}
          </span>
        </div>
      </div>

      {/* Filters */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div className="accrual-filters">
          <Input
            label="Competência"
            type="month"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
          />
          <Select label="Regime" value={regime} onChange={(e) => setRegime(e.target.value)}>
            {REGIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Select label="Tipo de operação" value={operationType} onChange={(e) => setOperationType(e.target.value)}>
            {OPERATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Select label="UF de origem" value={originUf} onChange={(e) => setOriginUf(e.target.value)}>
            <option value="">Todas</option>
            {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </Select>
          <Select label="UF de destino" value={destinationUf} onChange={(e) => setDestinationUf(e.target.value)}>
            <option value="">Todas</option>
            {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </Select>
        </div>
      </Card>

      {/* Summary cards */}
      {isLoading ? (
        <LoadingSkeleton count={4} height="96px" borderRadius="12px" />
      ) : error ? (
        <Card className="accrual-detail__error">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao apurar o período</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f' }}>
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <Button variant="primary" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      ) : (
        <div className="accrual-summary">
          <div className="accrual-summary__item">
            <span className="accrual-summary__label">Receita total (período)</span>
            <strong className="accrual-summary__value">{formatCurrency(data?.revenue.totalValue)}</strong>
          </div>
          <div className="accrual-summary__item">
            <span className="accrual-summary__label">Receita autorizada</span>
            <strong className="accrual-summary__value">{formatCurrency(data?.revenue.authorizedValue)}</strong>
          </div>
          <div className="accrual-summary__item">
            <span className="accrual-summary__label">Total de tributos apurados</span>
            <strong className="accrual-summary__value">{formatCurrency(data?.totals.totalTaxes)}</strong>
          </div>
          <div className="accrual-summary__item">
            <span className="accrual-summary__label">Alíquota efetiva</span>
            <strong className="accrual-summary__value">{formatPct(data?.totals.effectiveRatePct)}</strong>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <div className="accrual-detail__grid">
          {/* Regime summary */}
          <Card variant="outlined" padding="0">
            <CardHeader className="px-md pt-md">
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Resumo do regime</h3>
            </CardHeader>
            <div className="accrual-regime">
              <div className="accrual-regime__row">
                <span>Regime utilizado</span>
                <Badge size="sm" variant="info">{data?.regimeSummary.regime ?? EMPTY}</Badge>
              </div>
              <div className="accrual-regime__row">
                <span>Documentos avaliados</span>
                <strong>{formatCount(data?.regimeSummary.documentsEvaluated)}</strong>
              </div>
              <div className="accrual-regime__row">
                <span>Documentos autorizados</span>
                <strong>{formatCount(data?.regimeSummary.documentsAuthorized)}</strong>
              </div>
              <div className="accrual-regime__row">
                <span>Empresas</span>
                <strong>{formatCount(data?.regimeSummary.companies)}</strong>
              </div>
              <div className="accrual-regime__row">
                <span>Motor real utilizado</span>
                <Badge size="sm" variant={data?.engineUsed ? 'success' : 'neutral'}>
                  {data?.engineUsed ? 'Sim — recálculo por item' : 'Não — usou totais do documento'}
                </Badge>
              </div>
              <div className="accrual-regime__row">
                <span>Itens recalculados</span>
                <strong>{formatCount(data?.engineAudit.evaluatedItems)}</strong>
              </div>
              <div className="accrual-regime__row">
                <span>Regras avaliadas</span>
                <strong>{formatCount(data?.engineAudit.totalRulesEvaluated)}</strong>
              </div>
              {enginesErrors.length > 0 && (
                <div className="accrual-regime__errors">
                  {enginesErrors.map((e, i) => <small key={i}>⚠ {e}</small>)}
                </div>
              )}
            </div>
          </Card>

          {/* Revenue by CFOP */}
          <Card variant="outlined" padding="0">
            <CardHeader className="px-md pt-md">
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Receita por CFOP</h3>
            </CardHeader>
            {byCfop.length === 0 ? (
              <div className="accrual-detail__empty"><p className="m-0">Sem receita no período.</p></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="fiscal-table">
                  <thead>
                    <tr>
                      <th>CFOP</th>
                      <th align="right">Base (R$)</th>
                      <th align="right">Docs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCfop.map((c) => (
                      <tr key={c.cfop}>
                        <td className="tabular-nums"><strong>{c.cfop}</strong></td>
                        <td align="right" className="tabular-nums">{formatCurrency(c.base)}</td>
                        <td align="right" className="tabular-nums">{formatCount(c.documentCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Calculated obligations */}
      {!isLoading && !error && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {obligations.length} obrigações
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Obrigações calculadas</h3>
          </CardHeader>
          {obligations.length === 0 ? (
            <div className="accrual-detail__empty">
              <p className="m-0">Nenhuma obrigação apurada no período.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="fiscal-table">
                <thead>
                  <tr>
                    <th>Tributo</th>
                    <th align="right">Base (R$)</th>
                    <th align="right">Alíquota média</th>
                    <th align="right">Imposto (R$)</th>
                    <th>Participação</th>
                    <th align="right">Docs.</th>
                  </tr>
                </thead>
                <tbody>
                  {obligations.map((o) => (
                    <tr key={o.taxType}>
                      <td><strong>{o.label}</strong><small className="cell-sub">{o.taxType}</small></td>
                      <td align="right" className="tabular-nums">{formatCurrency(o.baseValue)}</td>
                      <td align="right" className="tabular-nums">{formatPct(o.ratePct)}</td>
                      <td align="right" className="tabular-nums">{formatCurrency(o.grossTax)}</td>
                      <td className="accrual-obligation__share">
                        <ProgressBar value={o.grossTax} max={maxObligation || 1} size="sm" className="accrual-obligation__bar" />
                      </td>
                      <td align="right" className="tabular-nums">{formatCount(o.documentCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="accrual-detail__audit">
            <small>
              {data?.engineUsed
                ? `Recálculo real do motor: ${formatCount(data?.engineAudit.evaluatedItems)} itens, ${formatCount(data?.engineAudit.totalRulesEvaluated)} regras avaliadas.`
                : 'Sem itens para recálculo — obrigações derivadas dos totais dos documentos autorizados.'}
            </small>
          </div>
        </Card>
      )}

      <style>{`
        .accrual-detail-page { padding: 1.5rem; }
        .accrual-detail__period { min-width: 220px; text-align: right; }
        .accrual-filters { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; }
        .accrual-filters > * { flex: 1; min-width: 160px; }
        .accrual-detail__error { padding: 2rem; text-align: center; display: grid; gap: 0.5rem; justify-items: center; color: #74777f; }
        .accrual-detail__empty { padding: 2rem; text-align: center; color: #74777f; font-size: 0.875rem; }
        .accrual-detail__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .accrual-detail__audit { padding: 0.75rem 1rem; color: #74777f; font-size: 0.75rem; border-top: 1px solid #eceef0; }
        .accrual-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
        .accrual-summary__item { background: #fff; border: 1px solid #e0e3e5; border-radius: 10px; padding: 1rem; display: flex; flex-direction: column; gap: 0.25rem; }
        .accrual-summary__label { color: #74777f; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; }
        .accrual-summary__value { font-size: 1.25rem; font-weight: 700; color: #002045; font-variant-numeric: tabular-nums; }
        .accrual-regime { padding: 0.25rem 1rem 1rem; }
        .accrual-regime__row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.5rem 0; border-bottom: 1px solid #eceef0; font-size: 0.875rem; }
        .accrual-regime__row:last-child { border-bottom: none; }
        .accrual-regime__row span { color: #43474e; }
        .accrual-regime__errors { display: flex; flex-direction: column; gap: 0.25rem; padding: 0.5rem 0; }
        .accrual-regime__errors small { color: #ba1a1a; }
        .accrual-obligation__share { display: flex; align-items: center; }
        .accrual-obligation__bar { flex: 1; max-width: 120px; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .cell-sub { display: block; color: #74777f; font-size: 0.75rem; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        @media (max-width: 1024px) { .accrual-detail__grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}